import { useEffect, useMemo, useState } from "react";
import { FaStore } from "react-icons/fa";
import {
  useGetFirmPerformanceReportMutation,
  useGetFirmRangePerformanceReportMutation,
} from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import useReportFirms from "../../../Hooks/useReportFirms";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import {
  ReportDownloadButtons,
  ReportPreview,
} from "../../../Components/Reports/PerformanceReportView";
import { CustomDropdown, DateField, FirmDropdown } from "../../../Components/atoms";
import { firmHasActiveProduct } from "../../../utils/consumptionProductGroup";
import { networkMinDate } from "../../../utils/creationInfo";
import {
  PERIOD_OPTIONS,
  PERIOD_DAY,
  PERIOD_MONTH,
  PERIOD_RANGE,
  PERIOD_YEAR,
  MODE_SPECIFIC,
  MODE_LAST_N,
  PRODUCT_OPTIONS,
  MONTH_NAMES,
  FIRST_YEAR,
  buildPerformanceRequest,
} from "./performanceReportOptions";
import { getFirmTypeStyle } from "../../../utils/firmTypeStyles";

/**
 * MÜŞTERİ PERFORMANS RAPORU (#338, sözleşme firm-performance-report.md).
 *
 * Dönem: Günlük / Aylık / Yıllık / Tarih Aralığı; Günlük / Aylık / Yıllık'ta
 * "Belirli" ya da "Son N". Firma seçimi her dönemde ÇOK SEÇİMLİ — seçilen
 * her firma alt müşteri / alt firma ağacıyla rapora girer (sunucu).
 *
 * Hangi uca ne gideceğine `buildPerformanceRequest` karar verir: belirli ay
 * → performance, geri kalanı (belirli gün dahil) → range-performance +
 * `period` (izin ve tablo sütun birimi sunucuda); tüm dönemler aynı PDF
 * şablonunu kullanır. Rapor
 * PDF'tir; yanıttaki `sheets` ile aynı içerik Excel olarak da iner.
 */
export default function FirmPerformanceUnifiedReportPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const { hasPermission } = usePermission();

  const today = useMemo(() => new Date(), []);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  // Kullanıcının göreceği dönemler — izni olmayan dönem hiç listelenmez.
  const periodOptions = useMemo(
    () => PERIOD_OPTIONS.filter((p) => hasPermission(p.permission)),
    [hasPermission],
  );

  const [period, setPeriod] = useState(() => periodOptions[0] || PERIOD_OPTIONS[0]);

  // İzin listesi auth yenilenince değişebilir; seçili dönem listeden düşerse
  // ilk izinli döneme çekilir.
  useEffect(() => {
    if (periodOptions.length === 0) return;
    if (!periodOptions.some((p) => p.value === period?.value)) {
      setPeriod(periodOptions[0]);
    }
  }, [periodOptions, period]);

  const periodValue = period?.value;
  const isRange = periodValue === PERIOD_RANGE;

  const [mode, setMode] = useState(MODE_SPECIFIC);
  const [lastN, setLastN] = useState(7);
  const [product, setProduct] = useState(PRODUCT_OPTIONS[0]);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [date, setDate] = useState(today);
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);
  const [startDate, setStartDate] = useState(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [endDate, setEndDate] = useState(today);

  // Pasif firmalar da listede (tüketimi raporlanır); "Tüm Firmalar" altındaki
  // "Pasif dahil et" ile topluca, ya da tek tek seçilir.
  const { firms, isLoading: firmDataIsLoading } = useReportFirms(token);

  /**
   * Firma listesi her dönemde aynı (#338): müşteri + bayi. Seçili üründe ürün
   * ayarı pasif olan ya da o ürünü hiç tanımlamamış müşteri listelenmez
   * (2026-08-28); bayinin kendi ürün ayarı ağının tüketimini yansıtmadığı
   * için süzgeç bayiye uygulanmaz (#200).
   */
  const selectableFirms = useMemo(
    () =>
      (firms || []).filter(
        (f) =>
          (f.firmType === "customer" || f.firmType === "dealer") &&
          (f.firmType === "dealer" || firmHasActiveProduct(f, product?.value)),
      ),
    [firms, product],
  );

  // Ürün değişince listede kalmayan seçim düşürülür (boş rapor üretmesin).
  useEffect(() => {
    const ids = new Set(selectableFirms.map((f) => f._id));
    setSelectedFirms((prev) => {
      const kept = prev.filter((f) => ids.has(f._id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [selectableFirms]);

  // Dönem değişince Son N, o dönemin aralığına çekilir.
  const lastNConfig = period?.lastN;
  useEffect(() => {
    if (!lastNConfig) return;
    setLastN((n) => Math.min(lastNConfig.max, Math.max(lastNConfig.min, n)));
  }, [lastNConfig]);

  const [getMonthlyReport, monthlyState] = useGetFirmPerformanceReportMutation();
  const [getRangeReport, rangeState] = useGetFirmRangePerformanceReportMutation();

  const request = buildPerformanceRequest({
    period: periodValue,
    mode: isRange ? MODE_SPECIFIC : mode,
    n: lastN,
    date,
    year,
    month,
    startDate,
    endDate,
    today,
  });
  const stateByKind = { monthly: monthlyState, range: rangeState };
  const activeState = stateByKind[request.kind] || rangeState;
  const { data, isLoading, error } = activeState;

  // Seçili firmaların en erken oluşturulma günü — öncesi seçilemez (#343)
  const minFirmDate = useMemo(() => {
    // Seçilen bayinin müşterileri / ana firmanın alt firmaları da rapora girer:
    // her seçimin ağındaki (alt + üst) en eski gün (#270)
    const days = selectedFirms.map((f) => networkMinDate(f, firms)).filter(Boolean);
    return days.length > 0 ? new Date(Math.min(...days.map((d) => d.getTime()))) : undefined;
  }, [selectedFirms, firms]);
  const minYear = minFirmDate ? Math.max(FIRST_YEAR, minFirmDate.getFullYear()) : FIRST_YEAR;

  const yearOptions = useMemo(() => {
    const options = [];
    for (let y = currentYear; y >= minYear; y -= 1) {
      options.push({ id: String(y), label: String(y), value: y });
    }
    return options;
  }, [currentYear, minYear]);

  // Cari yılda gelecek aylar seçilemez — sunucu da bunu ayrıca reddeder.
  const monthOptions = useMemo(() => {
    const lastMonth = year === currentYear ? currentMonth : 12;
    const firstMonth = minFirmDate && year === minFirmDate.getFullYear() ? minFirmDate.getMonth() + 1 : 1;
    return MONTH_NAMES.slice(0, lastMonth)
      .map((name, index) => ({ id: String(index + 1), label: name, value: index + 1 }))
      .filter((o) => o.value >= firstMonth);
  }, [year, currentYear, currentMonth, minFirmDate]);

  // Seçim oluşturulmadan önceye düştüyse ilk geçerli yıl / aya çekilir
  useEffect(() => {
    if (year < minYear) setYear(minYear);
    else if (monthOptions.length > 0 && !monthOptions.some((o) => o.value === month)) {
      setMonth(monthOptions[0].value);
    }
  }, [minYear, year, month, monthOptions]);

  const handleYearChange = (option) => {
    const nextYear = option.value;
    setYear(nextYear);
    if (nextYear === currentYear && month > currentMonth) setMonth(currentMonth);
  };

  const modeOptions = lastNConfig
    ? [
        { id: MODE_SPECIFIC, value: MODE_SPECIFIC, label: period.specificLabel },
        { id: MODE_LAST_N, value: MODE_LAST_N, label: `Son N ${lastNConfig.unit}` },
      ]
    : [];
  const lastNOptions = lastNConfig
    ? Array.from({ length: lastNConfig.max - lastNConfig.min + 1 }, (_, i) => {
        const n = lastNConfig.min + i;
        return { id: String(n), value: n, label: `Son ${n} ${lastNConfig.unit}` };
      })
    : [];

  const firmIds = selectedFirms.map((f) => f._id);
  const canSearch = (() => {
    if (!product?.value || firmIds.length === 0) return false;
    if (isRange) return Boolean(startDate && endDate);
    if (mode === MODE_LAST_N) return Boolean(lastN);
    if (periodValue === PERIOD_DAY) return Boolean(date);
    if (periodValue === PERIOD_MONTH) return Boolean(year && month);
    return Boolean(year);
  })();

  const handleSearch = () => {
    if (!canSearch) return;
    const body = { token, firmIds, productName: product.value, ...request.body };
    if (request.kind === "monthly") getMonthlyReport(body);
    else getRangeReport(body);
  };

  const handleClearFilters = () => {
    setProduct(PRODUCT_OPTIONS[0]);
    setSelectedFirms([]);
    setMode(MODE_SPECIFIC);
    setDate(today);
    setYear(currentYear);
    setMonth(currentMonth);
    setStartDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setEndDate(today);
    activeState.reset?.();
  };

  const report = data?.data;
  const pdfBase64 = report?.pdfBase64;

  const errorMessage =
    error?.data?.message || error?.error || "Rapor oluşturulurken bir hata oluştu.";

  const headerActions = <ReportDownloadButtons report={report} />;

  const activeFilters = [
    { label: "Dönem", value: `${period?.label} · ${request.label}` },
    ...(product ? [{ label: "Ürün", value: product.label }] : []),
    ...(selectedFirms.length > 0
      ? [
          {
            label: "Müşteri / Bayi",
            value:
              selectedFirms.length === 1 ? selectedFirms[0].firmName : `${selectedFirms.length} firma`,
          },
        ]
      : []),
  ];

  // İzinsiz kullanıcıya route zaten 403 verir; savunmacı boş durum.
  if (periodOptions.length === 0) {
    return (
      <ReportLayout title="Müşteri Performans Raporu" showExcel={false}>
        <ReportEmptyState
          title="Yetki Yok"
          message="Performans raporlarının hiçbiri için yetkiniz bulunmuyor."
          icon="document"
        />
      </ReportLayout>
    );
  }

  return (
    <ReportLayout
      title="Müşteri Performans Raporu"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Filtre Seçenekleri"
          description={period?.description || "Rapor için dönem, ürün ve müşteri seçin"}
          /* Tarih Aralığı dışındaki dönemler kendi alanını aşağıda kurar. */
          showDatePickers={isRange}
          minDate={minFirmDate}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isLoading}
          searchDisabled={!canSearch}
          searchDisabledMessage={period?.requiredMessage}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          activeFilters={activeFilters}
        >
          <div className="grid grid-cols-1 gap-4">
            <CustomDropdown
              label="Dönem"
              required
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              placeholder="Dönem seçin"
              showSearch={false}
            />

            <CustomDropdown
              label="Ürün"
              required
              value={product}
              onChange={(option) => {
                // Ürün değişince firma listesi daralıyor — seçim sıfırlanır.
                setProduct(option);
                setSelectedFirms([]);
              }}
              options={PRODUCT_OPTIONS}
              placeholder="Ürün seçin"
              showSearch={false}
            />

            <FirmDropdown
              label="Müşteriler / Bayiler"
              multiple
              value={selectedFirms}
              onChange={setSelectedFirms}
              firms={selectableFirms}
              loading={firmDataIsLoading}
              required
              placeholder="Müşteri ya da bayi seçin"
              inactiveToggle
            />

            {!isRange && (
              <div className="grid grid-cols-2 gap-4">
                <CustomDropdown
                  label="Seçim"
                  required
                  value={modeOptions.find((o) => o.value === mode)}
                  onChange={(option) => setMode(option.value)}
                  options={modeOptions}
                  showSearch={false}
                />
                {mode === MODE_LAST_N && (
                  <CustomDropdown
                    label="Kaç"
                    required
                    value={lastNOptions.find((o) => o.value === lastN)}
                    onChange={(option) => setLastN(option.value)}
                    options={lastNOptions}
                    showSearch={false}
                  />
                )}
              </div>
            )}

            {!isRange && mode === MODE_SPECIFIC && periodValue === PERIOD_DAY && (
              /* Tek gün — gelecek tarih seçilemez, sunucu da reddeder. */
              <DateField
                label="Tarih"
                required
                value={date}
                onChange={(d) => setDate(d)}
                disableFuture
                minDate={minFirmDate}
              />
            )}

            {!isRange && mode === MODE_SPECIFIC && periodValue === PERIOD_MONTH && (
              <div className="grid grid-cols-2 gap-4">
                <CustomDropdown
                  label="Yıl"
                  required
                  value={yearOptions.find((o) => o.value === year)}
                  onChange={handleYearChange}
                  options={yearOptions}
                  placeholder="Yıl"
                  showSearch={false}
                />
                <CustomDropdown
                  label="Ay"
                  required
                  value={monthOptions.find((o) => o.value === month)}
                  onChange={(option) => setMonth(option.value)}
                  options={monthOptions}
                  placeholder="Ay"
                  showSearch={false}
                />
              </div>
            )}

            {!isRange && mode === MODE_SPECIFIC && periodValue === PERIOD_YEAR && (
              <CustomDropdown
                label="Yıl"
                required
                value={yearOptions.find((o) => o.value === year)}
                onChange={(option) => setYear(option.value)}
                options={yearOptions}
                placeholder="Yıl"
                showSearch={false}
              />
            )}
          </div>

          {/* Seçilen firmalar — hangileri olduğu görünmeli ve tek tek
              çıkarılabilmeli. TEK satır, yatay kayar; uzun ad "..." ile kesilir. */}
          {selectedFirms.length > 0 && (
            <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 py-0.5">
              {selectedFirms.map((selected) => (
                <span
                  key={selected._id}
                  title={selected.firmName}
                  className={`inline-flex shrink-0 items-center gap-1.5 max-w-[200px] pl-3 pr-2 py-1.5 rounded-full border text-xs font-SemiBold ${getFirmTypeStyle(selected).badge}`}
                >
                  {selected.firmType === "dealer" && (
                    <FaStore className="w-3 h-3 shrink-0" aria-label="Bayi" />
                  )}
                  <span className="truncate">{selected.firmName}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedFirms((prev) => prev.filter((f) => f._id !== selected._id))
                    }
                    className="w-4 h-4 shrink-0 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
                    aria-label={`${selected.firmName} seçimini kaldır`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </ReportFilterCard>
      }
    >
      {isLoading && (
        <ReportLoadingState
          title="Performans Raporu Hazırlanıyor"
          description="Veriler yükleniyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={4}
        />
      )}

      {!isLoading && error && (
        <ReportErrorState title="Hata Oluştu" message={errorMessage} icon="warning" />
      )}

      {!isLoading && !error && pdfBase64 && <ReportPreview report={report} />}

      {!isLoading && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Rapor Bekleniyor"
          message={period?.emptyMessage || "Seçimleri yapıp 'Raporu Getir' butonuna basın."}
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
